import * as Hooks from 'preact/hooks';
import * as Selectors from '../store/selectors';
import { useSelector } from '../store/redux';
import { Ui } from '@dora/vivian';
import { Api, Fixtuur } from '@fixtuur/backpack';
import * as Arr from 'fp-ts/Array';
import { useEffectful } from './useEffectful';
import * as O from 'fp-ts/Option';
import { Effect, Record } from 'effect';
import { useStellaLayer } from './useStellaLayer';

export const useGetParts = (heroPart: Fixtuur.Part.Id | null): { parts: Ui.Part.Part[] } => {
    const stellaLayer = useStellaLayer();

    const tenantId = useSelector(Selectors.Core.tenant);

    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const allOptions = useSelector(Selectors.Options.allOptions);
    const productOptions = useSelector(Selectors.Options.productOptions);
    const productSelectionsByPart = useSelector(Selectors.Selections.productSelectionsByPart);

    const allGoods = useEffectful(
        () =>
            (tenantId && currentConfigurator && allOptions
                ? Fixtuur.Configuration.Good.fromProductOptions(
                      tenantId,
                      currentConfigurator,
                      allOptions,
                  )
                : Effect.succeed(undefined)
            ).pipe(Effect.provide(stellaLayer)),
        [tenantId, currentConfigurator, allOptions],
    ).result;

    const parts = Hooks.useMemo(() => {
        if (!allGoods || !currentConfigurator || !productOptions || !heroPart) return [];

        const compatibleGoods = Fixtuur.Configuration.Good.fromCacheByOptions(
            productOptions,
            allGoods,
            currentConfigurator,
        );

        const incompatibleGoodIds = Record.map(allGoods, (goods, part) =>
            Arr.compact(
                goods
                    .filter(
                        good =>
                            !(compatibleGoods[part] ?? []).find(cg =>
                                Fixtuur.Good.equals(cg, good),
                            ),
                    )
                    .map(Fixtuur.Good.id),
            ),
        );

        // cards are sorted by properties (dimensions then finish) by default
        const productCards = Ui.ProductCard.fromGoods(
            allGoods,
            productSelectionsByPart,
            incompatibleGoodIds,
        );

        return (
            Object.entries(productCards)
                // Filter out hero part from the parts list as it is displayed separately
                .filter(
                    ([partKey, cards]) =>
                        partKey !== heroPart &&
                        Api.ConfiguratorDefinition.shouldDisplayPart(
                            currentConfigurator,
                            partKey,
                        ) &&
                        cards.length > 0,
                )
                .map(([partKey, productCards]) => {
                    const label = currentConfigurator.partsMap[partKey].name;
                    const selectedProductCard = productCards.find(pc => pc.status === 'selected');
                    const thumbnail =
                        selectedProductCard &&
                        O.toUndefined(Fixtuur.Good.imageForPart(selectedProductCard.good));
                    const subtitle =
                        selectedProductCard &&
                        O.toUndefined(Fixtuur.Good.nameForPart(selectedProductCard.good));
                    return {
                        metadata: {
                            id: partKey,
                            label,
                            thumbnail,
                            subtitle,
                        },
                        productCards,
                    } satisfies Ui.Part.Part;
                })
        );
    }, [allGoods, currentConfigurator, productOptions, heroPart]);

    return { parts };
};
